import { useCallback, useEffect, useRef } from 'react'
import {
  armNativeLivePing,
  disarmNativeLivePing,
  isBackgroundLocationAvailable,
} from '../../../services/background-location'
import {
  getNativeLivePingCredentials,
  NATIVE_TOKEN_BACKGROUND_MIN_REMAINING_MS,
  NATIVE_TOKEN_MIN_REMAINING_MS,
} from '../data/sessions-api-env'

/** Re-arm at least this often while foregrounded even when the token is unchanged. */
export const NATIVE_LIVE_PING_REARM_MS = 5 * 60_000

/**
 * Keep the native layer armed to post LIVE en-route journey pings while the
 * app is backgrounded (provider navigating in Google Maps / phone locked),
 * so the member's ETA bracket and the "less than 5 min away" push stay live
 * when the WebView's own ping cadence is suspended.
 *
 * Hand-over: native posts ONLY while the app is not in the foreground; in the
 * foreground `useEnRouteGpsTracking` pings as before. The server stores an
 * exact re-sent fix once and rejects a backfilled fix native already posted.
 *
 * Auth: hands native a short-lived Cognito ID token (with ≥ 30 min left, see
 * NATIVE_TOKEN_MIN_REMAINING_MS) — never the refresh token. Refreshed on
 * enable, on every successful JS ping (`refresh`), on resume, and every
 * {@link NATIVE_LIVE_PING_REARM_MS} while the WebView runs. Going to the
 * background force-refreshes unless ≥ 50 min remain (best-effort: JS may be
 * suspended before it completes). If it expires
 * mid-drive native stops live posting and the resume backfill covers it.
 *
 * Mounted ONLY by the app-wide live-ping controller (`useLiveJourneyPings`),
 * never by a screen. Disarmed when the journey leaves en_route / the
 * controller unmounts (sign-out, account switch) / the session changes (and
 * natively on session end, the 14 h stop, re-target and 401).
 */
interface ArmRequest {
  /** Re-arm even if the token is unchanged and the re-arm interval has not elapsed. */
  force: boolean
  /** App is going to the background: demand a token with ≥ 50 min left. */
  background?: boolean
}

export function useNativeLivePing({
  enabled,
  sessionId,
}: {
  /** Provider travelling and the journey is `en_route`. */
  enabled: boolean
  sessionId: string
}): { refresh: () => void } {
  const active = enabled && isBackgroundLocationAvailable()
  /** Bumped on every enable/disable so a late arm from a stale run is undone. */
  const generationRef = useRef(0)
  const activeRef = useRef(active)
  const inFlightRef = useRef(false)
  /** A request that arrived mid-flight; replayed (merged) once the current one settles. */
  const pendingRef = useRef<ArmRequest | null>(null)
  const lastArmRef = useRef<{ idToken: string; at: number } | null>(null)
  activeRef.current = active

  const arm = useCallback(
    async ({ force, background = false }: ArmRequest): Promise<void> => {
      if (!activeRef.current) return
      if (inFlightRef.current) {
        const prev = pendingRef.current
        pendingRef.current = {
          force: (prev?.force ?? false) || force,
          background: (prev?.background ?? false) || background,
        }
        return
      }
      const generation = generationRef.current
      inFlightRef.current = true
      try {
        const creds = await getNativeLivePingCredentials(
          Date.now(),
          background ? NATIVE_TOKEN_BACKGROUND_MIN_REMAINING_MS : NATIVE_TOKEN_MIN_REMAINING_MS,
        )
        if (!creds || generation !== generationRef.current) return
        const last = lastArmRef.current
        if (!force && last && last.idToken === creds.idToken && Date.now() - last.at < NATIVE_LIVE_PING_REARM_MS) {
          return
        }
        const armed = await armNativeLivePing({ sessionId, ...creds })
        if (generation !== generationRef.current) {
          // Disabled while the arm was in flight: make sure nothing stays armed.
          if (armed) void disarmNativeLivePing()
          return
        }
        lastArmRef.current = armed ? { idToken: creds.idToken, at: Date.now() } : null
      } catch {
        /* signed out / offline — native keeps buffering for backfill */
      } finally {
        inFlightRef.current = false
        const pending = pendingRef.current
        pendingRef.current = null
        if (pending) await arm(pending)
      }
    },
    [sessionId],
  )

  useEffect(() => {
    if (!active) return
    const generation = ++generationRef.current
    lastArmRef.current = null
    void arm({ force: true })

    let removeAppListener: (() => void) | null = null
    let disposed = false
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void arm({ force: true })
    }
    document.addEventListener('visibilitychange', onVisibility)
    void import('@capacitor/app')
      .then(({ App }) =>
        App.addListener('appStateChange', ({ isActive }) => {
          // Going to the background: one last best-effort refresh (forced when
          // < 50 min remain) while JS still runs; coming back: re-arm.
          void arm(isActive ? { force: true } : { force: true, background: true })
        }),
      )
      .then((handle) => {
        if (disposed) void handle.remove()
        else removeAppListener = () => void handle.remove()
      })
      .catch(() => {})
    const interval = window.setInterval(() => void arm({ force: false }), NATIVE_LIVE_PING_REARM_MS)

    return () => {
      disposed = true
      if (generationRef.current === generation) generationRef.current += 1
      document.removeEventListener('visibilitychange', onVisibility)
      removeAppListener?.()
      window.clearInterval(interval)
      lastArmRef.current = null
      void disarmNativeLivePing()
    }
  }, [active, arm])

  const refresh = useCallback(() => {
    void arm({ force: false })
  }, [arm])

  return { refresh }
}
